import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/lib/useMediaQuery";

/**
 * Two-mode surface built on Radix Dialog.
 *
 *  - `side` (desktop, ≥768px): slides from the right, 560px wide, full-height.
 *  - `sheet` (mobile, <768px): slides from the bottom, fills the viewport.
 *
 * Callers don't usually pass `mode` directly — the built-in media query picks
 * the correct layout automatically. M6/M9 reuse this for the transaction
 * detail pane, so the API is intentionally general.
 */

export type DrawerMode = "side" | "sheet" | "auto";

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: DrawerMode;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Custom className on the inner content surface. */
  className?: string;
}

export function Drawer({
  open,
  onOpenChange,
  mode = "auto",
  title,
  description,
  children,
  className,
}: DrawerProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const resolvedMode: Exclude<DrawerMode, "auto"> =
    mode === "auto" ? (isDesktop ? "side" : "sheet") : mode;

  // Slide-in animations via keyframes defined in globals.css. Radix sets the
  // `data-state` attribute for us; we key frames off it so both open and
  // close animate. 200ms matches the spec motion token.
  const sideClasses =
    "fixed right-0 top-0 bottom-0 h-full w-[560px] max-w-full border-l border-[var(--line)] " +
    "data-[state=open]:animate-drawer-slide-in-right data-[state=closed]:animate-drawer-slide-out-right";

  const sheetClasses =
    "fixed inset-x-0 bottom-0 top-0 h-full w-full " +
    "data-[state=open]:animate-drawer-slide-in-bottom data-[state=closed]:animate-drawer-slide-out-bottom";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-[rgba(15,18,32,0.32)] backdrop-blur-[2px]",
            "data-[state=open]:animate-overlay-fade-in data-[state=closed]:animate-overlay-fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "z-50 flex flex-col bg-[var(--surface)] text-[var(--ink)] shadow-[var(--shadow-3)] focus:outline-none",
            resolvedMode === "side" ? sideClasses : sheetClasses,
            className,
          )}
        >
          {(title || description) && (
            <div className="flex items-start justify-between gap-4 border-b border-[var(--line)] py-3 pl-6 pr-3">
              <div className="flex min-w-0 flex-col gap-1 pt-2.5">
                {title && (
                  <Dialog.Title className="font-display text-[15px] font-semibold leading-tight tracking-tight text-[var(--ink)]">
                    {title}
                  </Dialog.Title>
                )}
                {description && (
                  <Dialog.Description className="text-[13px] text-[var(--ink-3)]">
                    {description}
                  </Dialog.Description>
                )}
              </div>
              <DrawerClose />
            </div>
          )}
          {!title && !description && (
            <div className="absolute right-2 top-2">
              <DrawerClose />
            </div>
          )}
          <div className="flex-1 overflow-y-auto">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Standalone close button. Kept 44×44 for tap-target compliance (spec §9.0).
 */
function DrawerClose() {
  return (
    <Dialog.Close
      aria-label="Close"
      className={cn(
        "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--r-md)]",
        "text-[var(--ink-3)] transition-colors duration-fast ease hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
      )}
    >
      <X className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden />
    </Dialog.Close>
  );
}

export const DrawerBody = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function DrawerBody({ className, ...props }, ref) {
  return <div ref={ref} className={cn("p-6", className)} {...props} />;
});
